import { useEffect, useMemo, useRef, useState } from 'react';
import { Chess } from 'chess.js';
import axios from 'axios';
import ChessBoard from './ChessBoard';
import Timer from './Timer';
import { API_ENDPOINTS } from '../../config';
import '../Styles/GameOver.css'

const inferLastMoveFromFens = (prevFen, nextFen) => {
  try {
    const base = new Chess(prevFen);
    const moves = base.moves({ verbose: true });

    for (const m of moves) {
      const t = new Chess(prevFen);
      t.move({
        from: m.from,
        to: m.to,
        promotion: m.promotion
      });

      if (t.fen() === nextFen) {
        return { from: m.from, to: m.to };
      }
    }
  } catch { }
  return null;
};

const pick = (obj, ...keys) => {
  for (const k of keys) {
    if (obj && obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
};

const getAuthHeader = () => {
  const raw = localStorage.getItem('JWToken');
  const token = raw ? String(raw).replace(/^Bearer\s+/i, '').trim() : '';
  return {
    headers: {
      Authorization: token ? `Bearer ${token}` : '',
      'Content-Type': 'application/json'
    }
  };
};

const playerNameToLetter = (p) => {
  const s = String(p ?? '').trim().toLowerCase();
  if (s === 'white') return 'w';
  if (s === 'black') return 'b';
  return '';
};

const oppositeLetter = (c) => (c === 'w' ? 'b' : c === 'b' ? 'w' : '');

function ChessGame({ settings, lobbyId, myUserId, whitePlayerId, connection, serverStatus, onMove }) {
  // --- ÚJ: Itt döntjük el, hogy be kell-e kapcsolni a High Contrast módot ---
  // Ha a settingsben a theme 'highContrast', akkor ez true lesz
  const isHighContrast = settings?.theme === 'highContrast';

  const serverFen = pick(serverStatus, 'fen', 'FEN');
  const serverCurrentPlayer = pick(serverStatus, 'currentPlayer', 'CurrentPlayer');
  const serverGameStatus = pick(serverStatus, 'gameStatus', 'GameStatus');
  const isInCheck = Boolean(pick(serverStatus, 'isInCheck', 'IsInCheck'));
  const isCheckmate = Boolean(pick(serverStatus, 'isCheckmate', 'IsCheckmate'));
  const isDraw = Boolean(pick(serverStatus, 'isDraw', 'IsDraw'));
  const whiteSeconds = pick(serverStatus, 'whiteTimeRemainingSeconds', 'WhiteTimeRemainingSeconds');
  const blackSeconds = pick(serverStatus, 'blackTimeRemainingSeconds', 'BlackTimeRemainingSeconds');

  const authoritativeWhiteUid = pick(serverStatus, 'whitePlayer', 'WhitePlayer') || whitePlayerId;
  const myColor = useMemo(() => {
    if (!myUserId || !authoritativeWhiteUid) return 'b';
    return String(myUserId).toLowerCase() === String(authoritativeWhiteUid).toLowerCase() ? 'w' : 'b';
  }, [myUserId, authoritativeWhiteUid]);
  const serverTurnLetter = playerNameToLetter(serverCurrentPlayer);
  const isOngoing = String(serverGameStatus ?? '').toLowerCase() === 'ongoing';
  const isMyTurn = isOngoing && serverTurnLetter && myColor === serverTurnLetter;
  const isWhiteActive = isOngoing && String(serverCurrentPlayer).toLowerCase() === 'white';
  const isBlackActive = isOngoing && String(serverCurrentPlayer).toLowerCase() === 'black';
  const isTurnTimedOut = isOngoing ? false : (!isCheckmate && !isDraw && (whiteSeconds === 0 || blackSeconds === 0));
  const isGameTimedOut = false;

  const [game, setGame] = useState(() => {
    try {
      return serverFen ? new Chess(serverFen) : new Chess();
    } catch {
      return new Chess();
    }
  });

  const [board, setBoard] = useState([]);
  const [selectedPiece, setSelectedPiece] = useState(null);
  const [statusText, setStatusText] = useState('');
  
  const [checkedSquare, setCheckedSquare] = useState(null);
  const [lastMove, setLastMove] = useState(null);
  const [moveHistory, setMoveHistory] = useState([]);
  const [promotionState, setPromotionState] = useState(null);
  const [validMoves, setValidMoves] = useState([]);
  const [showGameOver, setShowGameOver] = useState(false);
  const [outcomeText, setOutcomeText] = useState('');

  const gameRef = useRef(game);

  useEffect(() => {
    gameRef.current = game;
    const currentBoard = game.board();

    let kingInCheckPosition = null;

    if (isInCheck) {
      const turn = game.turn();
      currentBoard.forEach((row, rIndex) => {
        row.forEach((piece, cIndex) => {
          if (piece && piece.type === 'k' && piece.color === turn) {
            kingInCheckPosition = getAlgebraicNotation(rIndex, cIndex);
          }
        });
      });
    }
    setCheckedSquare(kingInCheckPosition);

    const formattedBoard = currentBoard.map((row) =>
      row.map((piece) => {
        if (!piece) return null;
        return { type: piece.type === 'p' ? 'pawn' : piece.type === 'n' ? 'knight' : piece.type === 'b' ? 'bishop' : piece.type === 'r' ? 'rook' : piece.type === 'q' ? 'queen' : 'king', color: piece.color === 'w' ? 'white' : 'black' };
      })
    );
    setBoard(formattedBoard);
  }, [game, isInCheck]);

  useEffect(() => {
    if (!serverFen) return;
    const currentFen = gameRef.current.fen();
    if (currentFen === serverFen) return;

    try {
      const tempGame = new Chess(currentFen);
      const moves = tempGame.moves({ verbose: true });
      let foundMove = null;

      for (const m of moves) {
        const testGame = new Chess(currentFen);
        testGame.move({ from: m.from, to: m.to, promotion: m.promotion });

        if (testGame.fen() === serverFen) {
          foundMove = m;
          break;
        }
      }

      if (foundMove) {
        const isCapture = foundMove.flags.includes('c') || foundMove.flags.includes('e');
        const moveInfo = {
          san: foundMove.san,
          from: foundMove.from,
          to: foundMove.to,
          color: foundMove.color,
          isCapture: isCapture
        };

        setLastMove({ from: foundMove.from, to: foundMove.to });

        const synced = new Chess(serverFen);
        setGame(synced);
        setSelectedPiece(null);
        setMoveHistory(prev => [...prev, moveInfo]);

        if (onMove) onMove(moveInfo);

      } else {
        const inferred = inferLastMoveFromFens(currentFen, serverFen);
        if (inferred) setLastMove(inferred);

        const synced = new Chess(serverFen);
        setGame(synced);
        setSelectedPiece(null);
        setPromotionState(null);
      }
    } catch {
      try {
        const synced = new Chess(serverFen);
        setGame(synced);
        setSelectedPiece(null);
        setPromotionState(null);
      } catch { }
    }
  }, [serverFen, onMove]);

  useEffect(() => {
    const current = serverTurnLetter === 'w' ? 'White' : serverTurnLetter === 'b' ? 'Black' : 'Unknown';
    const me = myColor === 'w' ? 'White' : 'Black';

    if (!serverStatus) {
      setStatusText(`You are ${me}.`);
      return;
    }

    if (isOngoing) {
      if (isInCheck) setStatusText(`Check! ${current} to move. (You are ${me})`);
      else setStatusText(`Current player: ${current}. (You are ${me})`);
      return;
    }

    if (isDraw) {
      setStatusText(`Game Over: Draw. (You are ${me})`);
      return;
    }

    if (isCheckmate) {
      const winner = oppositeLetter(serverTurnLetter);
      const winnerName = winner === 'w' ? 'White' : 'Black';
      const youWon = winner === myColor;
      setStatusText(`Game Over: ${winnerName} wins by checkmate. ${youWon ? '(You won)' : '(You lost)'}`);
      return;
    }

    if (isTurnTimedOut) {
      const loser = serverTurnLetter;
      const winner = oppositeLetter(loser);
      const winnerName = winner === 'w' ? 'White' : 'Black';
      const youWon = winner === myColor;
      setStatusText(`Game Over: ${winnerName} wins on time. ${youWon ? '(You won)' : '(You lost)'}`);
      return;
    }

    if (isGameTimedOut) {
      setStatusText(`Game Over: Match time limit reached. (Draw)`);
      return;
    }

    setStatusText(`Game Over.`);
  }, [serverStatus, isOngoing, isInCheck, isCheckmate, isDraw, isTurnTimedOut, isGameTimedOut, serverTurnLetter, myColor]);

  useEffect(() => {
    if (!connection) return;

    const handleReceiveMove = (moveData) => {
      try {
        const tempGame = new Chess(gameRef.current.fen());
        const result = tempGame.move({
          from: moveData.from,
          to: moveData.to,
          promotion: moveData.promotion || 'q'
        });
        if (result) {
          setGame(tempGame);
          setSelectedPiece(null);
          setPromotionState(null);

          const isCapture = result.flags.includes('c') || result.flags.includes('e');
          const moveInfo = {
            san: result.san,
            from: result.from,
            to: result.to,
            color: result.color,
            isCapture: isCapture
          };

          setLastMove({ from: result.from, to: result.to });
          setMoveHistory(prev => [...prev, moveInfo]);

          if (onMove) onMove(moveInfo);
        }
      } catch { }
    };

    connection.on('ReceiveMove', handleReceiveMove);
    return () => {
      connection.off('ReceiveMove', handleReceiveMove);
    };
  }, [connection, onMove]);

  useEffect(() => {
      if (!isOngoing && (isCheckmate || isDraw || isTurnTimedOut || isGameTimedOut)) {
          setShowGameOver(true);
        }
    }, [isOngoing, isCheckmate, isDraw, isTurnTimedOut, isGameTimedOut]);

  const getAlgebraicNotation = (row, col) => {
    const files = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'];
    const rank = 8 - row;
    return `${files[col]}${rank}`;
  };

  const executeMoveWithPromotion = async (from, to, promotionPiece) => {
    try {
      const tempGame = new Chess(game.fen());
      const moveAttempt = tempGame.move({ from, to, promotion: promotionPiece });

      if (!moveAttempt) {
        setSelectedPiece(null);
        setPromotionState(null);
        return;
      }

      const payload = {
        lobbyId,
        from,
        to,
        promotion: promotionPiece
      };

      const response = await axios.post(API_ENDPOINTS.MOVE, JSON.stringify(payload), getAuthHeader());

      if (response.status === 200) {
        const newGame = new Chess(game.fen());
        const applied = newGame.move({ from, to, promotion: promotionPiece });
        if (applied) {
          setGame(newGame);
          setSelectedPiece(null);
          setPromotionState(null);

          const isCapture = applied.flags.includes('c') || applied.flags.includes('e');
          const moveInfo = {
            san: applied.san,
            from: applied.from,
            to: applied.to,
            color: applied.color,
            isCapture: isCapture
          };

          setLastMove({ from: applied.from, to: applied.to });
          setMoveHistory(prev => [...prev, moveInfo]);

          if (onMove) onMove(moveInfo);
        }
      } else {
        setSelectedPiece(null);
        setPromotionState(null);
      }
    } catch {
      setSelectedPiece(null);
      setPromotionState(null);
    }
  };

  const handleSquareClick = async (row, col) => {
    if (!isOngoing) return;
    if (!isMyTurn) return;
    if (!lobbyId) return;

    const clickedPiece = board[row][col];
    const isWhiteTurnLocal = game.turn() === 'w';

    if (!selectedPiece || (clickedPiece && clickedPiece.color === (isWhiteTurnLocal ? 'white' : 'black'))) {
      if (clickedPiece && clickedPiece.color === (isWhiteTurnLocal ? 'white' : 'black')) {
        setSelectedPiece({ row, col, piece: clickedPiece });

        const moves = game.moves({
          square: getAlgebraicNotation(row, col),
          verbose: true
        });
        setValidMoves(moves.map(m => m.to));
      }
      return;
    }

    setValidMoves([]);

    const from = getAlgebraicNotation(selectedPiece.row, selectedPiece.col);
    const to = getAlgebraicNotation(row, col);

    try {
      const tempGame = new Chess(game.fen());
      const moveAttempt = tempGame.move({ from, to, promotion: 'q' });

      if (!moveAttempt) {
        setSelectedPiece(null);
        return;
      }

    const isPromotion = Boolean(moveAttempt.promotion) || String(moveAttempt.flags || '').includes('p');

    if (isPromotion) {
      setPromotionState({
        from,
        to,
        row,
        col
      });
    } else {
      await executeMoveWithPromotion(from, to, '');
    }
    } catch {
      setSelectedPiece(null);
    }
  };

  const handlePromotionSelect = async (piece) => {
    if (!promotionState) return;
    await executeMoveWithPromotion(promotionState.from, promotionState.to, piece);
  };

  useEffect(() => {
    if (!isCheckmate && !isDraw && !isTurnTimedOut && !isGameTimedOut) {
        return;
    }

    if (isDraw) {
        setOutcomeText(`Draw!`);
        return;
    }

    if (isCheckmate) {
       const winnerColor = oppositeLetter(serverTurnLetter);
       const youWon = winnerColor === myColor;

       setOutcomeText(youWon ? `You won by checkmate!` : `You lost by checkmate!`);
       return;
    }

    if (isTurnTimedOut) {
        const loserColor = serverTurnLetter;
        const winnerColor = oppositeLetter(loserColor);
        const youWon = winnerColor === myColor;

        setOutcomeText(youWon ? `You won by time!` : `You lost by time!`);
        return;
    }

    if (isGameTimedOut) {
       setOutcomeText(`Draw! (Match time limit reached.)`);
       return;
    }
}, [isCheckmate, isDraw, isTurnTimedOut, isGameTimedOut, serverTurnLetter, myColor]);

  return (
    // MODOSÍTVA: A 'theme-lock' osztály csak akkor kerül rá, ha 'highContrast' a téma!
    // Így működik együtt a Settings menüvel.
    <div 
        className={`chess-game-container d-flex flex-column h-100 w-100 ${isHighContrast ? 'theme-lock' : ''}`}
        data-bs-theme={isHighContrast ? "dark" : "light"}
        style={isHighContrast ? { colorScheme: "dark" } : {}}
    >
      
      <div className="d-flex flex-column px-3 py-2 bg-dark-transparent">
       <div className="d-flex justify-content-between align-items-center w-100">
            <Timer
              totalSeconds={whiteSeconds}
              isActive={isWhiteActive}
              label="White"
            />
            
            <Timer
              totalSeconds={blackSeconds}
              isActive={isBlackActive}
              label="Black"
            />
        </div>

        <div className="mt-2 small fw-bold text-white text-center">
          {statusText}
        </div>

      </div>

      <div className="flex-grow-1 d-flex align-items-center justify-content-center w-100" style={{ minHeight: 0, overflow: 'hidden' }}>
        <ChessBoard
          board={board}
          selectedPiece={selectedPiece}
          onSquareClick={handleSquareClick}
          settings={settings}
          validMoves={validMoves}
          checkedSquare={checkedSquare}
          lastMove={lastMove}
          promotionState={promotionState}
          onPromotionSelect={handlePromotionSelect}
          myColor={myColor}
        />
      </div>
          {showGameOver && (
              <div className="gameover-overlay">
                  <div className="gameover-modal">
                      <button className="close-btn" onClick={() => setShowGameOver(false)}>✕</button>
                      <h2>The game has ended!</h2>
                      <p>{outcomeText}</p>
                  </div>
              </div>
          )}
    </div>
  );
}

export default ChessGame;